import { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { IconButton, Menu, MenuItem, Box, FormControl, Select } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { getAllRoleAssignments } from './functions/get_all_role_assignments.function';
import { deleteRoleAssignment } from './functions/delete_role_assignment.function';
import { enrichRoleAssignments } from './functions/enrich_role_assignments.function';
import {
  formatRoleLevelFromRoleId,
  buildRoleByIdMap,
} from './functions/format_role_level_display.function';
import { getRoleAssignmentTableBaseOptions } from './role_assignment_table.utils';
import { RoleAssignmentTableShell } from './role_assignment_table_shell.component';
import { RoleAssignmentSearchToolbar } from './role_assignment_search_toolbar.component';

export const RoleAssignmentTable = forwardRef(
  (
    {
      onEdit,
      onDeleteSuccess,
      submitForApproval = false,
      users = [],
      roles = [],
      roleAssignments: rawRoleAssignments = [],
    },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [enrichedRoleAssignments, setEnrichedRoleAssignments] = useState([]);
    const [loading, setLoading] = useState(false);

    // Define regions
    const regions = useMemo(() => ['North', 'South', 'Central B', 'Central A'], []);

    // Function to fetch role assignments data (for refetch after add/update/delete)
    const fetchRoleAssignments = async () => {
      setLoading(true);
      try {
        const result = await getAllRoleAssignments();
        const responseArray = result?.response || result?.data || [];
        // Enrich role assignments with user data if users are available
        if (users.length > 0) {
          const enrichedAssignments = enrichRoleAssignments(responseArray, users, roles);
          setEnrichedRoleAssignments(enrichedAssignments);
        } else {
          setEnrichedRoleAssignments(responseArray);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // Enrich role assignments when users and/or assignments change.
    // Important: clear loading when users are loaded but assignments are still empty (API order),
    // otherwise the table spinner never stops (e.g. System Invoice Approvers tab).
    useEffect(() => {
      if (rawRoleAssignments.length > 0 && users.length > 0) {
        const enriched = enrichRoleAssignments(rawRoleAssignments, users, roles);
        setEnrichedRoleAssignments(enriched);
        setLoading(false);
      } else if (rawRoleAssignments.length > 0 && users.length === 0) {
        setLoading(true);
      } else if (users.length > 0 && rawRoleAssignments.length === 0) {
        setEnrichedRoleAssignments([]);
        setLoading(false);
      } else if (rawRoleAssignments.length === 0 && users.length === 0) {
        setEnrichedRoleAssignments([]);
        setLoading(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, roles, rawRoleAssignments]);

    // Function to add or update role assignment in the table
    const addOrUpdateRoleAssignment = (assignmentData, users, roles) => {
      // Find user and role data
      const user = users.find((u) => u.id === assignmentData.user_id);
      const roleById = buildRoleByIdMap(roles);

      // Create enriched role assignment object
      const enrichedAssignment = {
        id: assignmentData.id,
        user_id: assignmentData.user_id,
        role_id: assignmentData.role_id,
        user_name: user?.name || 'N/A',
        email: user?.email || 'N/A',
        region: user?.region || 'N/A',
        role_name: formatRoleLevelFromRoleId(
          assignmentData.role_name,
          assignmentData.role_id,
          roleById
        ),
        assigned_at: assignmentData.assigned_at,
        assigned_by: assignmentData.assigned_by,
      };

      setEnrichedRoleAssignments((prev) => {
        // Check if assignment already exists (for update)
        const existingIndex = prev.findIndex((item) => item.id === enrichedAssignment.id);
        if (existingIndex >= 0) {
          // Update existing assignment
          const updated = [...prev];
          updated[existingIndex] = enrichedAssignment;
          return updated;
        } else {
          // Add new assignment
          return [...prev, enrichedAssignment];
        }
      });
    };

    // Function to remove role assignment from the table
    const removeRoleAssignment = (id) => {
      setEnrichedRoleAssignments((prev) => prev.filter((item) => item.id !== id));
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      refetchRoleAssignments: fetchRoleAssignments,
      addOrUpdateRoleAssignment,
      removeRoleAssignment,
    }));

    const filteredRoleAssignments = useMemo(() => {
      let filtered = enrichedRoleAssignments;

      // Filter by region
      if (selectedRegion) {
        filtered = filtered.filter((assignment) => assignment.region === selectedRegion);
      }

      // Filter by search term
      if (searchTerm) {
        filtered = filtered.filter((assignment) =>
          Object.values(assignment).some((val) =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      }

      return filtered;
    }, [selectedRegion, searchTerm, enrichedRoleAssignments]);

    // eslint-disable-next-line react/prop-types
    const ActionsMenu = ({ row }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const [deleting, setDeleting] = useState(false);
      const open = Boolean(anchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleDelete = async () => {
        try {
          setDeleting(true);
          const result = await deleteRoleAssignment(row.original.id);
          if (result.success) {
            if (!submitForApproval) {
              removeRoleAssignment(row.original.id);
            }
            if (onDeleteSuccess) {
              onDeleteSuccess();
            }
          }
        } catch (err) {
          console.error('Delete failed:', err);
        } finally {
          setDeleting(false);
          handleClose();
        }
      };

      return (
        <>
          <IconButton onClick={handleClick} disabled={deleting}>
            <MoreVertIcon sx={{ fontWeight: 300, fontSize: '18px', color: '#667085' }} />
          </IconButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                // eslint-disable-next-line react/prop-types
                onEdit(row.original);
                handleClose();
              }}
              disabled={deleting}
            >
              Edit
            </MenuItem>
            <MenuItem onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Submitting...' : submitForApproval ? 'Request removal' : 'Delete'}
            </MenuItem>
          </Menu>
        </>
      );
    };

    const columns = useMemo(
      () => [
        {
          accessorKey: 'role_name',
          header: 'Levels',
          size: 200,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const roleName = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{roleName || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'user_name',
          header: 'Name',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const userName = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{userName || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'region',
          header: 'Region',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const region = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#667085' }}>{region || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'email',
          header: 'Email',
          size: 250,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const email = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#667085' }}>{email || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'assigned_by',
          header: 'Assigned By',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const assignedBy = cell.getValue();
            return (
              <span style={{ fontSize: '14px', color: '#667085' }}>{assignedBy || 'N/A'}</span>
            );
          },
        },
        {
          header: 'Actions',
          enableColumnActions: false,
          size: 50,
          // eslint-disable-next-line react/prop-types
          Cell: ({ row }) => <ActionsMenu row={row} />,
        },
      ],
      []
    );

    const table = useMaterialReactTable({
      ...getRoleAssignmentTableBaseOptions({
        initialState: {
          pagination: { pageSize: 10 },
          columnOrder: ['role_name', 'user_name', 'region', 'email', 'assigned_by', 'actions'],
        },
      }),
      columns,
      data: filteredRoleAssignments,
      state: { isLoading: loading },
      renderTopToolbarCustomActions: () => (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <RoleAssignmentSearchToolbar searchTerm={searchTerm} onSearchChange={setSearchTerm} />

          {/* Right side - Region dropdown */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Region Dropdown */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  color: '#757575',
                  borderRadius: '10px',
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                  },
                  '&:hover': {
                    border: '1px solid #ccc',
                  },
                  '&.Mui-focused': {
                    border: '1px solid #ccc',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    borderBottom: '1px solid #eee',
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                  }}
                >
                  REGIONS
                </MenuItem>
                {regions.map((region, index) => (
                  <MenuItem
                    key={region}
                    value={region}
                    sx={{
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                      borderBottom: index !== regions.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      ),
    });

    return (
      <RoleAssignmentTableShell>
        <MaterialReactTable table={table} />
      </RoleAssignmentTableShell>
    );
  }
);

RoleAssignmentTable.displayName = 'RoleAssignmentTable';

RoleAssignmentTable.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDeleteSuccess: PropTypes.func,
  submitForApproval: PropTypes.bool,
  users: PropTypes.array,
  roles: PropTypes.array,
  roleAssignments: PropTypes.array,
};
